import React, { useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Nav,
  Navbar,
  Row,
  Stack,
  Carousel,
} from "react-bootstrap";

/**
 * DollarPetInsurance_Landing_v03_Carousel
 * ----------------------------------------------------------------------------
 * Updates:
 * - Added owner's firstName and lastName.
 * - Made phone number required.
 * - Added Notes field at bottom of form.
 * - Test mode logs payload with meta fields.
 * - NEW: Hero background Carousel behind the form/content.
 * - UPDATED: Form aligned further right on desktop.
 */

const CLIENT_VERSION = "v03a" as const; // 2025-08-26
const TEST_MODE = false;
const API_URL = "aws.api.url";
const imagePath = "insurance/images/PET-Images/";

const heroImages = [
  { src: "AdobeStock_74093746_DOG-Preview.jpeg", alt: "Happy dogs at the park" },
  { src: "AdobeStock_1320490477_CAT-Preview.jpeg", alt: "Travel Cat" },
  { src: "AdobeStock_118001293_DOG-Preview-FLIP.jpg", alt: "Puppy and owner" },
  { src: "AdobeStock_207653094_DOG-Bandage-Preview.jpeg", alt: "Doggy at the Vet - bandage" },
  { src: "AdobeStock_769356417_CAT-Bandage-Preview-FLIP.jpg", alt: "Cat at the Vet - bandage" },
  { src: "AdobeStock_1596964140_CAT-COOL-Preview-FLIP.jpg", alt: "Cool CAT" },
];



const theme = {
  brand: {
    name: "Dollar Pet Insurance",
    domain: "DollarPetInsurance.com",
    primaryHex: "#198754",
    secondaryHex: "#0d6efd",
  },
  ui: {
    heroBgHeight: "min(80vh, 720px)",
    cardShadow: "0 8px 24px rgba(0,0,0,0.08)",
  },
} as const;

interface IQuote_DATA_Payload {
  firstName: string;
  lastName: string;
  petType: "dog" | "cat";
  petName?: string;
  breed?: string;
  age?: string;
  zip: string;
  email: string;
  phone: string;
  notes?: string;
  consent: boolean;
  terms: boolean;
  utm?: Record<string, string | undefined>;
  domainName: string;
  itemCategory: string;
  itemClass: string;
  itemType: string;
  clientVersion: string;
}

const isValidEmail = (v: string) => /.+@.+\..+/.test(v);
const digitsOnly = (v: string) => v.replace(/\D+/g, "");

const useUTM = () => {
  return useMemo(() => {
    if (typeof window === "undefined") return {} as Record<string, string>;
    const p = new URLSearchParams(window.location.search);
    const keys = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const;
    const out: Record<string, string> = {};
    keys.forEach((k) => {
      const val = p.get(k);
      if (val) out[k] = val;
    });
    return out;
  }, []);
};

export const PetInsuranceLanding_v03a_Carousel: React.FC = () => {
  const utm = useUTM();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [petType, setPetType] = useState<IQuote_DATA_Payload["petType"]>("dog");
  const [petName, setPetName] = useState("");
  const [breed, setBreed] = useState("");
  const [age, setAge] = useState("");
  const [zip, setZip] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [consent, setConsent] = useState(true);
  const [terms, setTerms] = useState(false);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const resetAlerts = () => {
    setError(null);
    setSuccess(null);
  };

  const validate = (): string | null => {
    if (!firstName) return "Please enter your first name.";
    if (!lastName) return "Please enter your last name.";
    if (!zip || digitsOnly(zip).length < 5) return "Please enter a valid 5-digit ZIP code.";
    if (!email || !isValidEmail(email)) return "Please enter a valid email address.";
    if (!phone || digitsOnly(phone).length < 10) return "Please enter a valid phone number.";
    if (!terms) return "Please accept the Terms to continue.";
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    resetAlerts();

    const validation = validate();
    if (validation) {
      setError(validation);
      return;
    }

    const payload_DATA: IQuote_DATA_Payload = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      petType,
      petName: petName.trim() || undefined,
      breed: breed.trim() || undefined,
      age: age.trim() || undefined,
      zip: digitsOnly(zip).slice(0, 5),
      email: email.trim(),
      phone: digitsOnly(phone),
      notes: notes.trim() || undefined,
      consent,
      terms,
      utm,
      domainName: String(window.location.hostname),
      itemCategory: "Customer.petInsurance.quote",
      itemClass: "quote.request",
      itemType: "pet.landingPage",
      clientVersion: String(CLIENT_VERSION),
    };

    try {
      setBusy(true);
      if (TEST_MODE) {
        console.log(payload_DATA);
        await new Promise((r) => setTimeout(r, 700));
        setSuccess("Thanks! We'll email your quote options shortly.");
      } else {
        const res = await fetch(API_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload_DATA),
        });
        if (!res.ok) throw new Error(`Request failed: ${res.status}`);
        setSuccess("Thanks! We'll email your quote options shortly.");
      }
    } catch (err: any) {
      console.error(err);
      setError(err?.message || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ background: "#fff" }}>
      {/* NAVBAR */}
      <Navbar bg="light" expand="lg" sticky="top" style={{ boxShadow: "0 2px 10px rgba(0,0,0,0.06)" }}>
        <Container>
          <Navbar.Brand href="#home" className="fw-bold">
            <span style={{ color: theme.brand.primaryHex }}>$</span> {theme.brand.name}
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="dpi-nav" />
          <Navbar.Collapse id="dpi-nav">
            <Nav className="ms-auto" navbarScroll>
              <Nav.Link href="#quote">Get Quote</Nav.Link>
              <Nav.Link href="#benefits">Benefits</Nav.Link>
              <Nav.Link href="#coverage">Coverage</Nav.Link>
              <Nav.Link href="#pricing">Pricing</Nav.Link>
              <Nav.Link href="#faq">FAQ</Nav.Link>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* HERO with BACKGROUND CAROUSEL + OVERLAID FORM/CONTENT */}
      <section id="home" className="py-5" style={{ position: "relative", overflow: "hidden" }}>
        {/* Background carousel */}
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            height: theme.ui.heroBgHeight,
            zIndex: 0,
          }}
        >
          <Carousel controls indicators interval={3000} fade>
            {heroImages.map((img, i) => (
              <Carousel.Item key={i}>
                <img
                  className="d-block w-100"
                  src={imagePath + img.src}
                  alt={img.alt}
                  style={{
                    height: theme.ui.heroBgHeight,
                    objectFit: "cover",
                    objectPosition: "center",
                    filter: "brightness(0.7)",
                  }}
                />
              </Carousel.Item>
            ))}
          </Carousel>
        </div>

        {/* Scrim overlay */}
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            height: theme.ui.heroBgHeight,
            background: "linear-gradient(0deg, rgba(0,0,0,0.35), rgba(0,0,0,0.15))",
            zIndex: 1,
            pointerEvents: "none",
          }}
        />

        {/* Foreground content */}
        <Container style={{ position: "relative", zIndex: 2 }}>
          <Row
            className="align-items-center g-4 justify-content-between"
            style={{ minHeight: theme.ui.heroBgHeight }}
          >
            {/* LEFT CONTENT */}
            <Col md={6} lg={5}>
              <h1 className="display-5 fw-bold mb-3 text-white">
                Affordable Pet Insurance — Simple, Fast, Trusted
              </h1>
              <p className="lead mb-4" style={{ color: "rgba(255,255,255,0.9)" }}>
                Protect your best friend from the unexpected. Transparent plans,
                flexible deductibles, and fast claims.
              </p>
              <Stack direction="horizontal" gap={3} className="mb-4">
                <Badge bg="success">Best for Budgets</Badge>
                <Badge bg="primary">Fast Claims</Badge>
                <Badge bg="secondary">Vet of Your Choice</Badge>
              </Stack>
              <div className="d-flex gap-2">
                <Button href="#quote" size="lg" variant="success">
                  Get My Quote
                </Button>
                <Button href="#benefits" size="lg" variant="outline-light">
                  See Benefits
                </Button>
              </div>
            </Col>

            {/* RIGHT FORM */}
            <Col md={6} lg={5} id="quote" className="ms-auto">
              <Card style={{ boxShadow: theme.ui.cardShadow, borderRadius: 14 }}>
                <Card.Body>
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <h5 className="m-0">Quick Quote</h5>
                    <small className="text-muted">
                      {CLIENT_VERSION}
                      {TEST_MODE ? " · Test" : ""}
                    </small>
                  </div>
                  {error && <Alert variant="danger" className="py-2">{error}</Alert>}
                  {success && <Alert variant="success" className="py-2">{success}</Alert>}

                  <Form onSubmit={handleSubmit}>
                    <Row className="g-3">
                      <Col md={6}>
                        <Form.Label>First Name</Form.Label>
                        <Form.Control
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          placeholder="John"
                        />
                      </Col>
                      <Col md={6}>
                        <Form.Label>Last Name</Form.Label>
                        <Form.Control
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                          placeholder="Doe"
                        />
                      </Col>

                      <Col md={6}>
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                          type="email"
                          placeholder="you@example.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                        />
                      </Col>
                      <Col md={6}>
                        <Form.Label>Phone (required)</Form.Label>
                        <Form.Control
                          inputMode="tel"
                          placeholder="(555) 555-5555"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          required
                        />
                      </Col>

                      <Col xs={12}>
                        <Form.Label>Pet Type</Form.Label>
                        <div className="d-flex gap-3">
                          <Form.Check
                            type="radio"
                            id="pet-dog"
                            name="petType"
                            label="Dog"
                            checked={petType === "dog"}
                            onChange={() => setPetType("dog")}
                          />
                          <Form.Check
                            type="radio"
                            id="pet-cat"
                            name="petType"
                            label="Cat"
                            checked={petType === "cat"}
                            onChange={() => setPetType("cat")}
                          />
                        </div>
                      </Col>

                      <Col md={6}>
                        <Form.Label>Pet Name (optional)</Form.Label>
                        <Form.Control
                          value={petName}
                          onChange={(e) => setPetName(e.target.value)}
                          placeholder={petType === "dog" ? "Buddy" : "Luna"}
                        />
                      </Col>
                      <Col md={6}>
                        <Form.Label>Breed (optional)</Form.Label>
                        <Form.Control
                          value={breed}
                          onChange={(e) => setBreed(e.target.value)}
                          placeholder={petType === "dog" ? "Labrador" : "Siamese"}
                        />
                      </Col>

                      <Col md={6}>
                        <Form.Label>Age (optional)</Form.Label>
                        <Form.Select value={age} onChange={(e) => setAge(e.target.value)}>
                          <option value="">Select age</option>
                          <option value="0-1">0–1 years</option>
                          <option value="2-5">2–5 years</option>
                          <option value="6-8">6–8 years</option>
                          <option value="9+">9+ years</option>
                        </Form.Select>
                      </Col>
                      <Col md={6}>
                        <Form.Label>ZIP Code</Form.Label>
                        <Form.Control
                          inputMode="numeric"
                          placeholder="80301"
                          value={zip}
                          onChange={(e) => setZip(e.target.value)}
                        />
                      </Col>

                      <Col xs={12}>
                        <Form.Label>Notes (optional)</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                          placeholder="Any special instructions or notes about your pet"
                        />
                      </Col>

                      <Col xs={12}>
                        <Form.Check
                          id="consent"
                          type="checkbox"
                          label="I agree to be contacted about my quote (email/SMS)."
                          checked={consent}
                          onChange={(e) => setConsent(e.currentTarget.checked)}
                        />
                        <Form.Check
                          id="terms"
                          type="checkbox"
                          label={<span>I accept the <a href="#">Terms</a> & <a href="#">Privacy</a>.</span>}
                          checked={terms}
                          onChange={(e) => setTerms(e.currentTarget.checked)}
                        />
                      </Col>

                      <Col xs={12} className="d-grid">
                        <Button type="submit" size="lg" variant="success" disabled={busy}>
                          {busy ? "Submitting…" : "See My Options"}
                        </Button>
                      </Col>
                    </Row>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default PetInsuranceLanding_v03a_Carousel;
